import React, { useState, useEffect, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoadingScreen from './container/Loading';
import Header from './container/Header';
import './styles/styles.css';
import './styles/inputs.css';

// Lazy load your components (MainPage loaded eagerly so "/" works when chunks fail on some devices)
import MainPage from './container/Auth/MainPage';


const SignInPage = lazy(() => import('./container/Auth/SignIn'));
const Dashboard = lazy(() => import('./container/Dashboard'));
const DashboardLayout = lazy(() => import('./container/Dashboard/DashboardLayout'));
const AvailableSoon = lazy(() => import('./container/AvailableSoon'));
const Calendar = lazy(() => import('./container/Schedule'));
const Monthly = lazy(() => import('./container/Monthly'));
const SignUp = lazy(() => import('./container/Auth/SignUp'));
const Weekly = lazy(() => import('./container/Weekly'));
const Profile = lazy(() => import('./container/Profile'));
const DashboardTeams = lazy(() => import('./container/Teams'));
const _AdminSettings = lazy(() => import('./container/AdminSettings'));
const TeamSettingsPage = lazy(() => import('./container/AdminSettings/TeamSettingsPage'));
const ForgottenPassword = lazy(() => import('./container/Auth/ForgotPassword'));
const Salaries = lazy(() => import('./container/Salary'));
const Leaves = lazy(() => import('./container/Leaves'));
const Test = lazy(() => import('./container/AvailableSoon'));
const RedirectToTeamSettings = lazy(() =>
  import('./container/AdminSettings/RedirectToTeamSettings').then((m) => ({ default: m.RedirectToTeamSettings }))
);
const LocaleRedirect = lazy(() => import('./container/Auth/LocaleRedirect'));
const ApiHealth = lazy(() => import('./container/ApiHealth'));
const POS = lazy(() => import('./container/POS'));
const SuperAdmin = lazy(() => import('./container/SuperAdmin'));


function AppContent() {
  const [isNightMode, setIsNightMode] = useState(() => {
    try {
      const storedValue = localStorage.getItem('isNightMode');
      return storedValue !== null ? JSON.parse(storedValue) : true;
    } catch {
      return true;
    }
  });

  const [isOnline, setIsOnline] = useState(navigator.onLine); // Track online status

  const toggleNightMode = () => {
    const newMode = !isNightMode;
    setIsNightMode(newMode);
    try {
      localStorage.setItem('isNightMode', JSON.stringify(newMode));
    } catch {
      // ignore when storage is unavailable
    }
  };

  useEffect(() => {
    const handleOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    // Clean up event listeners
    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  /* Sync theme to html so body and global bg use same tokens as dashboard */
  useEffect(() => {
    const theme = isNightMode ? 'dark' : 'light';
    document.documentElement.className = isNightMode ? 'night' : 'day';
    document.documentElement.setAttribute('data-theme', theme);
    // Notify native app of theme via WebView bridge
    console.log('theme:' + theme);
  }, [isNightMode]);

  const location = useLocation();
  const isPOSRoute = location.pathname === '/pos';

  if (!isOnline) {
    return <LoadingScreen />; // Show loading screen when offline
  }

  return (
    <>
      {!isPOSRoute && <Header toggleNightMode={toggleNightMode} isNightMode={isNightMode} />}
      <ToastContainer />
      <main className="app-main">
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
          <Route path="/" element={<MainPage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/fr" element={<LocaleRedirect />} />
          <Route path="/nl" element={<LocaleRedirect />} />
          <Route path="/confirmation" element={<SignInPage />} />
          <Route path="/forgot-password" element={<ForgottenPassword />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/subscribe" element={<Navigate to="/signup" replace />} />
          <Route path="/test" element={<Test />} />
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="settings" element={<Navigate to="/dashboard/profile" replace />} />
            <Route path="schedule" element={<Calendar />} />
            <Route path="team-schedule" element={<Monthly />} />
            <Route path="weekly" element={<Weekly />} />
            <Route path="team/:teamId" element={<TeamSettingsPage />} />
            <Route path="rates" element={<Salaries />} />
            <Route path="leaves" element={<Leaves />} />
            <Route path="profile" element={<Profile />} />
            <Route path="teams" element={<DashboardTeams />} />
            <Route path="ai-schedule" element={<AvailableSoon />} />
            <Route path="functionalities" element={<AvailableSoon />} />
            <Route path="onboarding-rating" element={<AvailableSoon />} />
            <Route path="generate-test-members" element={<AvailableSoon />} />
            <Route path="api-health" element={<ApiHealth />} />
            <Route path="super-admin" element={<SuperAdmin />} />

            <Route path="schedule-v2" element={<Navigate to="/dashboard/schedule" replace />} />
          </Route>
          {/* Legacy redirects to new dashboard paths */}
          <Route path="/calendar" element={<Navigate to="/dashboard/schedule" replace />} />
          <Route path="/monthly" element={<Navigate to="/dashboard/team-schedule" replace />} />
          <Route path="/weekly" element={<Navigate to="/dashboard/weekly" replace />} />
          <Route path="/profile" element={<Navigate to="/dashboard/profile" replace />} />
          <Route path="/admin-settings" element={<RedirectToTeamSettings />} />
          <Route path="/salaries" element={<Navigate to="/dashboard/rates" replace />} />
          <Route path="/pos" element={<POS />} />
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
    </>
  );
}

function App() {
  return (
    <Router>
      <I18nextProvider i18n={i18n}>
        <AppContent />
      </I18nextProvider>
    </Router>
  );
}

export default App;
